import React, { useState, useRef, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';

// ── Dark form palette ─────────────────────────────────────────────────────────

const F = {
  bg:          '#14141a',
  panel:       '#1e1e28',
  border:      'rgba(255,255,255,0.1)',
  input:       '#12121a',
  inputBorder: 'rgba(255,255,255,0.15)',
};

// ── Type tabs ─────────────────────────────────────────────────────────────────

const TYPES = ['products', 'services', 'orders', 'bookings'];

const TYPE_LABELS = {
  products: { nl: 'Producten',   en: 'Products'  },
  services: { nl: 'Diensten',    en: 'Services'  },
  orders:   { nl: 'Bestellingen',en: 'Orders'    },
  bookings: { nl: 'Boekingen',   en: 'Bookings'  },
};

// ── CSV Templates ─────────────────────────────────────────────────────────────

const CSV_TEMPLATES = {
  products: `name,sku,description,price,sale_price,vat_pct,currency,stock_qty,category,status\nExample Product,SKU-001,A great product,29.99,,21,EUR,100,General,active`,
  services: `name,description,duration_min,price,vat_pct,status\nPersonal Training,1-on-1 coaching session,60,75.00,21,active`,
  orders:   `order_ref,customer_name,customer_email,customer_phone,product_name,qty,unit_price,vat_pct,status\nORD-001,Jan de Vries,jan@example.com,+31612345678,Example Product,2,29.99,21,pending`,
  bookings: `service_name,customer_name,customer_email,customer_phone,start_time,end_time,status,notes\nPersonal Training,Jan de Vries,jan@example.com,+31612345678,2026-04-01 09:00,2026-04-01 10:00,confirmed,`,
};

// ── Platform definitions ──────────────────────────────────────────────────────

const PLATFORMS = [
  {
    key:         'shopify',
    icon:        '🛍️',
    name:        'Shopify',
    description: { nl: 'Importeer producten en bestellingen vanuit je Shopify-winkel.', en: 'Import products and orders from your Shopify store.' },
    color:       '#96BF48',
    supports:    ['products', 'orders'],
    credentials: [
      { key: 'shop',  label: { nl: 'Winkeldomein (bijv. mystore.myshopify.com)', en: 'Shop domain (e.g. mystore.myshopify.com)' }, type: 'text'     },
      { key: 'token', label: { nl: 'Admin API toegangstoken',                    en: 'Admin API Access Token'                    }, type: 'password' },
    ],
  },
  {
    key:         'woocommerce',
    icon:        '🛒',
    name:        'WooCommerce',
    description: { nl: 'Verbind met je WooCommerce-webshop om producten en bestellingen te importeren.', en: 'Connect to your WooCommerce store to import products and orders.' },
    color:       '#7F54B3',
    supports:    ['products', 'orders'],
    credentials: [
      { key: 'url',    label: { nl: 'Winkel-URL (https://mijnwinkel.nl)', en: 'Store URL (https://mystore.com)' }, type: 'text'     },
      { key: 'key',    label: { nl: 'Consumer Key',                       en: 'Consumer Key'                   }, type: 'text'     },
      { key: 'secret', label: { nl: 'Consumer Secret',                    en: 'Consumer Secret'                }, type: 'password' },
    ],
  },
  {
    key:         'hubspot',
    icon:        '🔶',
    name:        'HubSpot',
    description: { nl: 'Importeer deals als bestellingen vanuit HubSpot CRM.', en: 'Import deals as orders from HubSpot CRM.' },
    color:       '#FF7A59',
    supports:    ['orders'],
    credentials: [
      { key: 'token', label: { nl: 'Private App toegangstoken', en: 'Private App Access Token' }, type: 'password' },
    ],
  },
  {
    key:         'calendly',
    icon:        '📅',
    name:        'Calendly',
    description: { nl: 'Haal geplande afspraken op als boekingen vanuit Calendly.', en: 'Pull scheduled events as bookings from Calendly.' },
    color:       '#006BFF',
    supports:    ['bookings'],
    credentials: [
      { key: 'token', label: { nl: 'Persoonlijk toegangstoken', en: 'Personal Access Token' }, type: 'password' },
    ],
  },
  {
    key:         'acuity',
    icon:        '🗓️',
    name:        'Acuity Scheduling',
    description: { nl: 'Importeer afspraken en diensten vanuit Acuity Scheduling.', en: 'Import appointments and services from Acuity Scheduling.' },
    color:       '#38C9A8',
    supports:    ['bookings', 'services'],
    credentials: [
      { key: 'user_id', label: { nl: 'Gebruikers-ID', en: 'User ID'  }, type: 'text'     },
      { key: 'api_key', label: { nl: 'API-sleutel',   en: 'API Key'  }, type: 'password' },
    ],
  },
  {
    key:         'mindbody',
    icon:        '💪',
    name:        'Mindbody',
    description: { nl: 'Importeer diensten en boekingen vanuit Mindbody.', en: 'Import services and bookings from Mindbody.' },
    color:       '#00B4A6',
    supports:    ['services', 'bookings'],
    credentials: [
      { key: 'api_key', label: { nl: 'API-sleutel', en: 'API Key' }, type: 'password' },
      { key: 'site_id', label: { nl: 'Site-ID',     en: 'Site ID' }, type: 'text'     },
    ],
  },
];

// ── CSV parser ────────────────────────────────────────────────────────────────

function parseCsv(text) {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim() !== '');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.trim());
  const records = [];
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim());
    const obj = {};
    headers.forEach((h, idx) => {
      obj[h] = values[idx] !== undefined ? values[idx] : '';
    });
    records.push(obj);
  }
  return records;
}

// ── Download helper ───────────────────────────────────────────────────────────

function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Preview table ─────────────────────────────────────────────────────────────

function PreviewTable({ records, nl }) {
  if (!records || records.length === 0) return null;
  const cols    = Object.keys(records[0]).slice(0, 6);
  const rows    = records.slice(0, 10);
  const noData  = nl ? 'Geen gegevens' : 'No data';

  return (
    <div style={{ overflowX: 'auto', marginTop: 12 }}>
      <table style={{
        width: '100%', borderCollapse: 'collapse', fontSize: 12,
        color: '#ccd', background: F.bg, borderRadius: 6, overflow: 'hidden',
      }}>
        <thead>
          <tr>
            {cols.map(c => (
              <th key={c} style={{
                padding: '7px 10px', textAlign: 'left',
                background: '#1a1a24', color: _T.accent,
                borderBottom: `1px solid ${F.border}`, whiteSpace: 'nowrap',
                fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={cols.length} style={{ padding: '10px', textAlign: 'center', color: '#556' }}>
                {noData}
              </td>
            </tr>
          ) : rows.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? F.bg : '#16161e' }}>
              {cols.map(c => (
                <td key={c} style={{
                  padding: '6px 10px', borderBottom: `1px solid ${F.border}`,
                  maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {row[c] !== undefined && row[c] !== '' ? String(row[c]) : <span style={{ color: '#445' }}>—</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {records.length > 10 && (
        <div style={{ fontSize: 11, color: '#667', marginTop: 5 }}>
          {nl ? `… en nog ${records.length - 10} rijen` : `… and ${records.length - 10} more rows`}
        </div>
      )}
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────────────

function Spinner() {
  return (
    <span style={{
      display: 'inline-block', width: 16, height: 16,
      border: `2px solid ${F.border}`,
      borderTopColor: _T.accent,
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      verticalAlign: 'middle', marginLeft: 8,
    }} />
  );
}

// ── Result message ────────────────────────────────────────────────────────────

function ResultMsg({ result }) {
  if (!result) return null;
  const isErr = result.type === 'error';
  return (
    <div style={{
      marginTop: 10, padding: '9px 14px', borderRadius: 7,
      background: isErr ? 'rgba(180,40,40,0.18)' : 'rgba(60,180,90,0.15)',
      border: `1px solid ${isErr ? 'rgba(200,60,60,0.4)' : 'rgba(60,180,90,0.4)'}`,
      color: isErr ? '#f87' : '#6e4',
      fontSize: 13,
    }}>
      {result.msg}
    </div>
  );
}

// ── CSV Import Section ────────────────────────────────────────────────────────

function CsvImportSection({ type, businessId, nl, canImport }) {
  const fileRef           = useRef(null);
  const [records, setRecords]       = useState([]);
  const [importing, setImporting]   = useState(false);
  const [result, setResult]         = useState(null);

  const handleTemplate = () => {
    downloadCsv(`template_${type}.csv`, CSV_TEMPLATES[type]);
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const parsed = parseCsv(ev.target.result);
      setRecords(parsed);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!records.length) return;
    setImporting(true);
    setResult(null);
    try {
      const res  = await fetch(`/api/businesses/${businessId}/import-apply`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ type, records }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || (nl ? 'Onbekende fout' : 'Unknown error'));
      setResult({ type: 'ok', msg: nl ? `✓ ${data.imported} records geïmporteerd` : `✓ ${data.imported} records imported` });
      setRecords([]);
    } catch (err) {
      setResult({ type: 'error', msg: `✗ ${err.message}` });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{
      background: F.panel, border: `1px solid ${F.border}`,
      borderRadius: 10, padding: '18px 20px', marginBottom: 20,
    }}>
      <div style={{ fontWeight: 600, color: _T.accent, fontSize: 14, marginBottom: 14 }}>
        {nl ? 'CSV Importeren' : 'CSV Import'}
      </div>

      {/* Buttons row */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={handleTemplate} style={btnStyle('secondary')}>
          {nl ? '⬇ Download sjabloon' : '⬇ Download Template'}
        </button>
        {canImport && (
          <button onClick={() => fileRef.current && fileRef.current.click()} style={btnStyle('primary')}>
            {nl ? '📂 CSV-bestand selecteren' : '📂 Select CSV File'}
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </div>

      {/* Preview */}
      {records.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, color: '#889', marginBottom: 6 }}>
            {nl
              ? `Voorbeeld — eerste ${Math.min(10, records.length)} van ${records.length} rijen`
              : `Preview — first ${Math.min(10, records.length)} of ${records.length} rows`}
          </div>
          <PreviewTable records={records} nl={nl} />
          {canImport && (
            <button
              onClick={handleImport}
              disabled={importing}
              style={{ ...btnStyle('accent'), marginTop: 12 }}
            >
              {importing
                ? (nl ? 'Importeren…' : 'Importing…')
                : (nl ? `Importeer ${records.length} records` : `Import ${records.length} records`)}
              {importing && <Spinner />}
            </button>
          )}
        </div>
      )}

      <ResultMsg result={result} />
    </div>
  );
}

// ── Platform Card ─────────────────────────────────────────────────────────────

function PlatformCard({ platform, activeType, businessId, nl, canImport }) {
  const [open, setOpen]             = useState(false);
  const [creds, setCreds]           = useState({});
  const [fetching, setFetching]     = useState(false);
  const [fetchResult, setFetchResult] = useState(null);
  const [records, setRecords]       = useState([]);
  const [importing, setImporting]   = useState(false);
  const [importResult, setImportResult] = useState(null);

  const supported = platform.supports.includes(activeType);

  const handleCred = (key, val) => {
    setCreds(prev => ({ ...prev, [key]: val }));
  };

  const handleFetch = async () => {
    setFetching(true);
    setFetchResult(null);
    setRecords([]);
    try {
      const res  = await fetch(`/api/businesses/${businessId}/import-platform`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ platform: platform.key, type: activeType, credentials: creds }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || (nl ? 'Ophalen mislukt' : 'Fetch failed'));
      const recs = data.records || [];
      setRecords(recs);
      setFetchResult({
        type: 'ok',
        msg:  nl
          ? `✓ ${recs.length} records opgehaald van ${platform.name}`
          : `✓ ${recs.length} records fetched from ${platform.name}`,
      });
    } catch (err) {
      setFetchResult({ type: 'error', msg: `✗ ${err.message}` });
    } finally {
      setFetching(false);
    }
  };

  const handleImport = async () => {
    if (!records.length) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res  = await fetch(`/api/businesses/${businessId}/import-apply`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ type: activeType, records }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || (nl ? 'Importeren mislukt' : 'Import failed'));
      setImportResult({
        type: 'ok',
        msg:  nl
          ? `✓ ${data.imported} records geïmporteerd`
          : `✓ ${data.imported} records imported`,
      });
      setRecords([]);
    } catch (err) {
      setImportResult({ type: 'error', msg: `✗ ${err.message}` });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{
      background: F.panel,
      border: `1px solid ${open ? platform.color + '55' : F.border}`,
      borderRadius: 10,
      padding: '16px 18px',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      transition: 'border-color 0.2s',
      opacity: supported ? 1 : 0.45,
    }}>
      {/* Card header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 42, height: 42, borderRadius: 10,
          background: platform.color + '22',
          border: `1px solid ${platform.color}44`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 22, flexShrink: 0,
        }}>
          {platform.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: '#eef', fontSize: 14 }}>{platform.name}</div>
          <div style={{ fontSize: 11, color: '#778', marginTop: 2, lineHeight: 1.4 }}>
            {platform.description[nl ? 'nl' : 'en']}
          </div>
        </div>
      </div>

      {/* Supported types badges */}
      <div style={{ display: 'flex', gap: 5, marginTop: 10, flexWrap: 'wrap' }}>
        {platform.supports.map(s => (
          <span key={s} style={{
            padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600,
            background: s === activeType ? platform.color + '33' : 'rgba(255,255,255,0.05)',
            color:      s === activeType ? platform.color        : '#667',
            border:     `1px solid ${s === activeType ? platform.color + '55' : 'rgba(255,255,255,0.07)'}`,
            textTransform: 'capitalize',
          }}>
            {TYPE_LABELS[s][nl ? 'nl' : 'en']}
          </span>
        ))}
      </div>

      {/* Connect button */}
      {supported && canImport && (
        <button
          onClick={() => { setOpen(o => !o); setFetchResult(null); setImportResult(null); setRecords([]); }}
          style={{
            ...btnStyle('outline'),
            marginTop: 12,
            borderColor: open ? platform.color + '88' : F.border,
            color:       open ? platform.color        : '#aab',
          }}
        >
          {open
            ? (nl ? 'Sluiten' : 'Close')
            : (nl ? 'Verbinden' : 'Connect')}
        </button>
      )}
      {!supported && (
        <div style={{ marginTop: 10, fontSize: 11, color: '#556' }}>
          {nl
            ? `Niet beschikbaar voor ${TYPE_LABELS[activeType].nl}`
            : `Not available for ${TYPE_LABELS[activeType].en}`}
        </div>
      )}

      {/* Expanded credential form */}
      {open && supported && (
        <div style={{ marginTop: 14, borderTop: `1px solid ${F.border}`, paddingTop: 14 }}>
          {platform.credentials.map(field => (
            <div key={field.key} style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#889', marginBottom: 4 }}>
                {field.label[nl ? 'nl' : 'en']}
              </label>
              <input
                type={field.type || 'text'}
                value={creds[field.key] || ''}
                onChange={e => handleCred(field.key, e.target.value)}
                autoComplete="off"
                style={{
                  width: '100%', padding: '8px 10px', borderRadius: 6,
                  background: F.input, border: `1px solid ${F.inputBorder}`,
                  color: '#dde', fontSize: 13, outline: 'none',
                  fontFamily: field.type === 'password' ? 'monospace' : 'inherit',
                }}
              />
            </div>
          ))}
          <button
            onClick={handleFetch}
            disabled={fetching}
            style={{ ...btnStyle('accent'), marginTop: 4 }}
          >
            {fetching
              ? (nl ? `Ophalen van ${platform.name}…` : `Fetching from ${platform.name}…`)
              : (nl ? `Ophalen van ${platform.name}` : `Fetch from ${platform.name}`)}
            {fetching && <Spinner />}
          </button>

          <ResultMsg result={fetchResult} />

          {/* Fetched records preview */}
          {records.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, color: '#889', marginBottom: 6 }}>
                {nl
                  ? `Voorbeeld — eerste ${Math.min(10, records.length)} van ${records.length} rijen`
                  : `Preview — first ${Math.min(10, records.length)} of ${records.length} rows`}
              </div>
              <PreviewTable records={records} nl={nl} />
              <button
                onClick={handleImport}
                disabled={importing}
                style={{ ...btnStyle('primary'), marginTop: 12 }}
              >
                {importing
                  ? (nl ? 'Importeren…' : 'Importing…')
                  : (nl ? `Importeer ${records.length} records` : `Import ${records.length} records`)}
                {importing && <Spinner />}
              </button>
              <ResultMsg result={importResult} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Button style helpers ──────────────────────────────────────────────────────

function btnStyle(variant) {
  const base = {
    border: 'none', borderRadius: 7, padding: '8px 16px',
    fontSize: 13, fontWeight: 600, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
    transition: 'opacity 0.15s, box-shadow 0.15s',
    outline: 'none',
  };
  if (variant === 'primary') return { ...base, background: _T.accent + '22', color: _T.accent, border: `1px solid ${_T.accent}55` };
  if (variant === 'accent')  return { ...base, background: _T.accent,        color: '#000',     fontWeight: 700 };
  if (variant === 'secondary') return { ...base, background: 'rgba(255,255,255,0.06)', color: '#aab', border: `1px solid ${F.border}` };
  if (variant === 'outline') return { ...base, background: 'transparent', color: '#aab', border: `1px solid ${F.border}` };
  return base;
}

// ── Main screen ───────────────────────────────────────────────────────────────

export default function BusinessImportScreen({ lang, businessId, role }) {
  const nl       = lang === 'nl';
  const canImport = ['owner', 'admin'].includes(role);
  const [activeType, setActiveType] = useState('products');

  return (
    <div style={{
      fontFamily: "'Inter', sans-serif",
      background: F.bg,
      minHeight: '100vh',
      padding: '24px 20px',
      color: '#dde',
    }}>
      <style>{CSS}</style>

      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#eef' }}>
          {nl ? 'Importeren' : 'Import'}
        </h2>
        <div style={{ fontSize: 13, color: '#778', marginTop: 4 }}>
          {nl
            ? 'Importeer gegevens vanuit CSV of externe platforms.'
            : 'Import data from CSV or external platforms.'}
        </div>
        {!canImport && (
          <div style={{
            marginTop: 10, padding: '8px 14px', borderRadius: 7,
            background: 'rgba(180,120,40,0.15)', border: '1px solid rgba(200,150,50,0.3)',
            color: '#cc9', fontSize: 12,
          }}>
            {nl
              ? 'Je hebt alleen-lezen toegang. Importeren vereist owner- of admin-rechten.'
              : 'You have read-only access. Importing requires owner or admin role.'}
          </div>
        )}
      </div>

      {/* Type tab bar */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: `1px solid ${F.border}`,
        marginBottom: 24, overflowX: 'auto',
      }}>
        {TYPES.map(t => {
          const active = t === activeType;
          return (
            <button
              key={t}
              onClick={() => setActiveType(t)}
              style={{
                background: 'transparent', border: 'none',
                padding: '10px 18px', cursor: 'pointer',
                fontSize: 13, fontWeight: active ? 700 : 500,
                color:    active ? _T.accent : '#778',
                borderBottom: active ? `2px solid ${_T.accent}` : '2px solid transparent',
                transition: 'color 0.15s, border-color 0.15s',
                whiteSpace: 'nowrap', outline: 'none',
              }}
            >
              {TYPE_LABELS[t][nl ? 'nl' : 'en']}
            </button>
          );
        })}
      </div>

      {/* CSV Import */}
      <CsvImportSection
        type={activeType}
        businessId={businessId}
        nl={nl}
        canImport={canImport}
      />

      {/* Platform Import section */}
      <div style={{ fontWeight: 600, color: '#aab', fontSize: 13, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {nl ? 'Platform-import' : 'Platform Import'}
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
        gap: 14,
      }}>
        {PLATFORMS.map(p => (
          <PlatformCard
            key={p.key}
            platform={p}
            activeType={activeType}
            businessId={businessId}
            nl={nl}
            canImport={canImport}
          />
        ))}
      </div>
    </div>
  );
}
